/*
 * Copyright (c) 2023. Collabro Limited
 */

import React, { useCallback } from "react";
import {
  defaultRangeExtractor,
  elementScroll,
  Range,
  useVirtualizer,
  VirtualItem,
  VirtualizerOptions,
} from "@tanstack/react-virtual";

const VirtualTable = ({
  columns,
  data,
  getPersonnelColumnWidth,
}: {
  columns: any;
  data: any;
  getPersonnelColumnWidth: any;
}) => {
  const parentRef = React.useRef<HTMLDivElement | null>(null);

  const parentOffsetRef = React.useRef(0);

  const scrollingRef = React.useRef<number>();

  React.useLayoutEffect(() => {
    parentOffsetRef.current = parentRef.current?.offsetTop ?? 0;
  }, []);
  const easeInOutQuint = (t: any) => {
    return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
  };
  const getColumnWidth = (index: number) => columns[index].width;
  const scrollToFn: VirtualizerOptions<any, any>["scrollToFn"] =
    React.useCallback((offset, canSmooth, instance) => {
      const duration = 1000;
      const start = parentRef.current!.scrollTop;
      const startTime = (scrollingRef.current = Date.now());

      const run = () => {
        if (scrollingRef.current !== startTime) return;
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = easeInOutQuint(Math.min(elapsed / duration, 1));
        const interpolated = start + (offset - start) * progress;

        if (elapsed < duration) {
          elementScroll(interpolated, canSmooth, instance);
          requestAnimationFrame(run);
        } else {
          elementScroll(interpolated, canSmooth, instance);
        }
      };
      requestAnimationFrame(run);
    }, []);
  const virtualizer = useVirtualizer({
    count: data.length,
    estimateSize: () => 50,
    overscan: 5,
    getScrollElement: () => parentRef.current,
    scrollMargin: parentOffsetRef.current,
    scrollToFn,
    rangeExtractor: useCallback((range: Range) => {
      const next = new Set([0, ...defaultRangeExtractor(range)]);
      return [...next].sort((a, b) => a - b);
    }, []),
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: columns.length,
    getScrollElement: () => parentRef.current,
    estimateSize: getColumnWidth,
    overscan: 10,
    rangeExtractor: useCallback((range: Range) => {
      const next = new Set([0, ...defaultRangeExtractor(range)]);
      return [...next].sort((a, b) => a - b);
    }, []),
  });
  const columnItems = columnVirtualizer.getVirtualItems();
  const [before, after] =
    columnItems.length > 0
      ? [
          columnItems[1].start > columnItems[0].end
            ? columnItems[1].start - columnItems[0].end
            : 0,
          columnVirtualizer.getTotalSize() -
            columnItems[columnItems.length - 1].end,
        ]
      : [0, 0];

  const generateCells = (column: VirtualItem, row: VirtualItem) => {
    // Options
    if (row.index === 0 && column.index === 0) {
      return (
        <div
          key={column.key}
          style={{
            minHeight: 50,
            width: getColumnWidth(column.index),
            minWidth: getColumnWidth(column.index),
            position: "sticky",
            left: 0,
            background: "#fff",
            zIndex: 1,
            borderBottom: "1px solid #c8c8c8",
            borderRight: "1px solid #c8c8c8",
            padding: 5,
            display: "flex",
            alignItems: "center",
          }}
        >
          <>{columns[column.index].name}</>
        </div>
      );
    } else if (row.index === 0) {
      // Date Headers
      return (
        <div
          key={column.key}
          style={{
            minHeight: 50,
            width: getColumnWidth(column.index),
            minWidth: getColumnWidth(column.index),
            position: "initial",
            left: 0,
            background: "#fff",
            zIndex: 0,
            borderBottom: "1px solid #c8c8c8",
            borderRight: "none",
            padding: 5,
            display: "flex",
            alignItems: "center",
          }}
        >
          <>{columns[column.index].name}</>
        </div>
      );
    } else if (column.index === 0) {
      //Personnel Cell
      return (
        <div
          key={column.key}
          style={{
            minHeight: row.size,
            // width:getColumnWidth(column.index),
            width: "100%",
            minWidth: getColumnWidth(column.index),
            maxWidth: getColumnWidth(column.index),
            position: "sticky",
            left: 0,
            background: "#fff",
            zIndex: 1,
            borderBottom: "1px solid #c8c8c8",
            borderRight: "1px solid #c8c8c8",
            padding: 5,
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              maxWidth: getPersonnelColumnWidth(),
              flexWrap: "wrap",
            }}
          >
            {data[row.index][column.index]}
          </div>
        </div>
      );
    } else {
      //Movement Cell
      return (
        <div
          key={column.key}
          style={{
            minHeight: row.size,
            width: getColumnWidth(column.index),
            minWidth: getColumnWidth(column.index),
            background: "rgb(245 245 245)",
            borderBottom: "1px solid #c8c8c8",
            display: "flex",
            alignItems: "center",
          }}
        >
          {data[row.index][column.index]}
        </div>
      );
    }
  };

  return (
    <div
      ref={parentRef}
      style={{
        overflow: "auto",
        width: "100%",
        // @ts-ignore
        "--scrollbar-margin": `${getPersonnelColumnWidth()}px`,
      }}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: "100%",
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((row) => {
          return (
            <div
              key={row.key}
              data-index={row.index}
              ref={virtualizer.measureElement}
              style={{
                position: row.index === 0 ? "sticky" : "absolute",
                zIndex: row.index === 0 ? 3 : 0,
                width: "fit-content",
                background: "#fff",
                top: 0,
                left: 0,
                transform: `translateY(${
                  row.start - virtualizer.options.scrollMargin
                }px)`,
                display: "flex",
              }}
            >
              <div style={{ width: `${before}px` }} />
              {columnItems.map((column) => {
                return generateCells(column, row);
              })}
              <div style={{ width: `${after}px` }} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(VirtualTable);
